/* What a change of station identity means for the board.
 *
 * Every tt_* table is authenticated-only, so who the station is decides what
 * the server will hand back — and an empty answer to a signed-out request looks
 * exactly like an empty answer to a signed-in one. The board cannot tell those
 * apart by looking at the rows, so it has to remember who asked.
 *
 * Three states, and the third is the one that causes the trouble:
 *
 *   'checking'   the stored session is still being restored. Not a synonym for
 *                signed out — at boot it is usually what is true for the first
 *                moment, and calling it signed out would settle the question
 *                before anyone has answered it.
 *   'anon'       nobody is signed in.
 *   'user:<id>'  a station account.
 */

/* Should the board's own tables be fetched again?
 *
 *   current   who the station is now
 *   loadedAs  who it was when the last full load went out, or null if it never has
 *
 * The rules, and what each is for:
 *
 *   still checking      no. Asking now would ask as nobody.
 *   signed out          no. There is nothing a signed-out fetch could come back
 *                       with — every one of these tables would refuse it — so the
 *                       request would be a round trip to be told what we already
 *                       know. The shell puts the sign-in gate up instead; see
 *                       `app.js`.
 *   signed in, changed  yes. This is the sign-in case: the boot load ran as
 *                       'checking' or 'anon' and fetched an empty day, and
 *                       without this the station sits in front of an empty board
 *                       having just watched itself sign in successfully.
 *   signed in, same     no. On the ordinary boot the stored session is already
 *                       back before the first load, so this is the same identity
 *                       and nothing is fetched twice.
 */
export function shouldReloadBoard(current, loadedAs) {
  if (current === 'checking') return false;
  if (current === 'anon') return false;
  return current !== loadedAs;
}

/* Is a failed load worth telling anybody about?
 *
 *   loadedAs  who the station was when that load went out
 *
 * The board's tables are authenticated-only, so a load that goes out with no
 * session comes back `permission denied for table tt_services` — and the console
 * used to report that verbatim, as *"Could not reach Supabase"*. Every word of
 * which is wrong. Supabase was reached, understood the question, and gave the
 * correct answer to it: you are nobody, and nobody may read this. That is the
 * system working, and dressing it up as an outage sends whoever reads it looking
 * at the wifi.
 *
 * So a failure is only reported when there was a session to fail. Signed out —
 * or still restoring, which can also race a boot load out of the door without a
 * token — the answer is not an error, it is the gate, and the gate says it
 * better than any banner could.
 *
 * This deliberately does not inspect the error. A signed-out load fails at the
 * first table it touches, and which table that happens to be is an ordering
 * detail; matching on the message would make the console's error handling depend
 * on the order of the columns in `fetchParts`.
 */
export function shouldReportLoadFailure(loadedAs) {
  return loadedAs !== 'anon' && loadedAs !== 'checking';
}
